import { getDashboardPageConfig } from '@/constants/dashboardPages'
import type { SalesDashboardMisFilter } from '@/composables/salesDashboardInjection'
import type { DashboardPagesQueryParams } from '@/utils/salesDashboardPagesRequest'
import type {
  SalesDashboardCustomRange,
  SalesDashboardFilters,
  SalesDashboardSalesRepOption,
  SalesFilterOptions,
  SalesMode,
  SalesTimeRangePreset,
} from '@/types/salesDashboardShared'
import type { SdPerformancePeriod } from '@/types/salesDashboardSession'
import type {
  GroupedCheckboxMultiSelectGroup,
  GroupedCheckboxMultiSelectValue,
} from '@/components/forms/GroupedCheckboxMultiSelect.vue'
import { getTodayOtsDateString } from '@/utils/datePresets'

/** Default custom range for sales dashboard pages: Jan 1 (OTS year) through today. */
export function getDefaultSalesDashboardCustomRange(): SalesDashboardCustomRange {
  const today = getTodayOtsDateString()
  const year = today.slice(0, 4)
  return { from: `${year}-01-01`, to: today }
}

/** Display YYYY-MM-DD as MM/DD/YYYY for Sales Dashboard custom range summaries. */
export function formatSalesDashboardCustomDateDisplay(isoYmd: string): string {
  if (!isoYmd || !/^\d{4}-\d{2}-\d{2}$/.test(isoYmd)) return isoYmd
  const [year, month, day] = isoYmd.split('-')
  return `${month}/${day}/${year}`
}
import {
  defaultCreatedStatuses,
  type SalesDashboardStatusOptions,
} from '@/utils/salesDashboardStatusDefaults'

export const SALES_INVOICED_DEFAULT_PATTERN = /^(exported|completed|closed)$/i

/** @deprecated Use SALES_INVOICED_DEFAULT_PATTERN */
export const PERFORMANCE_INVOICED_DEFAULT_PATTERN = SALES_INVOICED_DEFAULT_PATTERN

export const SALES_STATUS_GROUP_ORDER = 'order_status'
export const SALES_STATUS_GROUP_PRODUCT = 'product_status'
export const SALES_STATUS_GROUP_INVOICE = 'invoice_status'
export const SALES_STATUS_GROUP_GL = 'gl_code'
export const SALES_FILTER_GROUP_TEAMS = 'commercial_teams'
export const SALES_FILTER_GROUP_REPS = 'sales_reps'
export const SALES_FILTER_GROUP_CUSTOMER_STATE = 'customer_state'
export const SALES_FILTER_GROUP_ACCOUNTS = 'account_groups'

/** @deprecated Use SALES_STATUS_GROUP_ORDER */
export const PERFORMANCE_STATUS_GROUP_ORDER = SALES_STATUS_GROUP_ORDER
/** @deprecated Use SALES_STATUS_GROUP_PRODUCT */
export const PERFORMANCE_STATUS_GROUP_PRODUCT = SALES_STATUS_GROUP_PRODUCT
/** @deprecated Use SALES_STATUS_GROUP_INVOICE */
export const PERFORMANCE_STATUS_GROUP_INVOICE = SALES_STATUS_GROUP_INVOICE
/** @deprecated Use SALES_STATUS_GROUP_GL */
export const PERFORMANCE_STATUS_GROUP_GL = SALES_STATUS_GROUP_GL

export interface SalesDashboardDateRange {
  startDate: string
  endDate: string
}

/** @deprecated Use SalesDashboardDateRange */
export type PerformanceDateRange = SalesDashboardDateRange

export type SalesDashboardFilterPage =
  | 'performance'
  | 'leaderboard'
  | 'sale-detail'
  | 'amazon-dashboard'
  | 'open-pipeline'

function formatDateYmd(year: number, month: number, day: number): string {
  const m = String(month).padStart(2, '0')
  const d = String(day).padStart(2, '0')
  return `${year}-${m}-${d}`
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function salesTimeRangeToPerformancePeriod(
  timeRange: SalesTimeRangePreset
): SdPerformancePeriod {
  if (timeRange === 'ytd') return 'ytd'
  if (timeRange === 'ttm') return 'r12'
  return 'mtd'
}

export function performancePeriodToSalesTimeRange(
  period: SdPerformancePeriod
): SalesTimeRangePreset {
  if (period === 'ytd') return 'ytd'
  if (period === 'r12') return 'ttm'
  return 'month'
}

/**
 * Date window for legacy performance period helpers (NY timezone).
 */
export function performancePeriodToDateRange(
  period: SdPerformancePeriod,
  selectedMonth: string
): SalesDashboardDateRange {
  const todayStr = getTodayOtsDateString()
  const [todayYear, todayMonth] = todayStr.split('-').map(Number)

  if (period === 'ytd') {
    return {
      startDate: formatDateYmd(todayYear, 1, 1),
      endDate: todayStr,
    }
  }

  if (period === 'r12') {
    const startMonthDate = new Date(todayYear, todayMonth - 1 - 11, 1)
    return {
      startDate: formatDateYmd(startMonthDate.getFullYear(), startMonthDate.getMonth() + 1, 1),
      endDate: todayStr,
    }
  }

  const [yearStr, monthStr] = selectedMonth.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const monthStart = formatDateYmd(year, month, 1)

  if (year > todayYear || (year === todayYear && month > todayMonth)) {
    return { startDate: monthStart, endDate: monthStart }
  }

  if (year === todayYear && month === todayMonth) {
    return { startDate: monthStart, endDate: todayStr }
  }

  return {
    startDate: monthStart,
    endDate: formatDateYmd(year, month, lastDayOfMonth(year, month)),
  }
}

export function cloneSalesDashboardFilters(filters: SalesDashboardFilters): SalesDashboardFilters {
  return {
    mis: filters.mis,
    teamUuids: [...filters.teamUuids],
    teamMemberUuids: [...filters.teamMemberUuids],
    repKeys: [...filters.repKeys],
    orderStatuses: [...filters.orderStatuses],
    productStatuses: [...filters.productStatuses],
    invoiceStatuses: [...filters.invoiceStatuses],
    glCodes: [...filters.glCodes],
    customerStates: [...filters.customerStates],
    accountGroups: [...filters.accountGroups],
  }
}

/** @deprecated Use cloneSalesDashboardFilters */
export const clonePerformanceFilters = cloneSalesDashboardFilters

export function parseMisSuffixedFilterLabel(token: string): string {
  const trimmed = token.trim()
  const match = trimmed.match(/^(.+?) \((PIQ|CB)\)$/i)
  return match ? match[1].trim() : trimmed
}

export function defaultSalesInvoicedStatuses(invoiceOptions: string[]): string[] {
  return invoiceOptions.filter((s) =>
    SALES_INVOICED_DEFAULT_PATTERN.test(parseMisSuffixedFilterLabel(s))
  )
}

/** @deprecated Use defaultSalesInvoicedStatuses */
export const defaultPerformanceInvoicedStatuses = defaultSalesInvoicedStatuses

export function buildDefaultSalesStatusFilters(
  options: SalesDashboardStatusOptions
): Pick<SalesDashboardFilters, 'orderStatuses' | 'productStatuses' | 'invoiceStatuses'> {
  const { order } = defaultCreatedStatuses(options.order, options.product)
  return {
    orderStatuses: order,
    productStatuses: [...options.product],
    invoiceStatuses: defaultSalesInvoicedStatuses(options.invoice),
  }
}

/** @deprecated Use buildDefaultSalesStatusFilters */
export const buildDefaultPerformanceStatusFilters = buildDefaultSalesStatusFilters

/** Resolve a rep key (canonical `repKey` or any `filterKeys` alias) to its rep option. */
function buildRepKeyLookup(
  salesReps: SalesDashboardSalesRepOption[]
): (key: string) => SalesDashboardSalesRepOption | undefined {
  const byCanonicalKey = new Map(salesReps.map((rep) => [rep.repKey, rep]))
  const byAnyFilterKey = new Map<string, SalesDashboardSalesRepOption>()
  for (const rep of salesReps) {
    for (const key of rep.filterKeys) {
      byAnyFilterKey.set(key, rep)
    }
  }
  return (key) => byCanonicalKey.get(key) ?? byAnyFilterKey.get(key)
}

export function expandRepKeysForApi(
  repKeys: string[],
  salesReps: SalesDashboardSalesRepOption[]
): string[] {
  if (repKeys.length === 0) return []

  const lookupRep = buildRepKeyLookup(salesReps)
  const expanded = new Set<string>()
  for (const key of repKeys) {
    const rep = lookupRep(key)
    if (rep && rep.filterKeys.length > 0) {
      for (const filterKey of rep.filterKeys) expanded.add(filterKey)
    } else {
      expanded.add(key)
    }
  }
  return [...expanded]
}

/**
 * Map rep keys (canonical or aliases like `name:…`) to the canonical `repKey`
 * known to the filter options, so rep filter dropdowns recognize the selection.
 * Keys with no matching rep are kept as-is.
 */
export function canonicalizeRepKeys(
  repKeys: string[],
  salesReps: SalesDashboardSalesRepOption[]
): string[] {
  if (repKeys.length === 0) return []

  const lookupRep = buildRepKeyLookup(salesReps)
  const canonical = new Set<string>()
  for (const key of repKeys) {
    const rep = lookupRep(key)
    canonical.add(rep ? rep.repKey : key)
  }
  return [...canonical]
}

/**
 * Normalize API customer_states from a flat array or country-keyed object into flat
 * codes plus grouped-by-country metadata for the Customer State filter UI.
 */
function normalizeCustomerStatesFromApi(
  rawStates: string[] | Record<string, string[]> | undefined
): { customerStates: string[]; customerStatesByCountry: Record<string, string[]> } {
  if (Array.isArray(rawStates)) {
    const customerStates = [...new Set(rawStates)].sort()
    return {
      customerStates,
      customerStatesByCountry: customerStates.length > 0 ? { States: customerStates } : {},
    }
  }

  const customerStatesByCountry: Record<string, string[]> = {}
  for (const [country, states] of Object.entries(rawStates ?? {})) {
    if (!Array.isArray(states)) continue
    const normalized = [...new Set(states)].sort()
    if (normalized.length > 0) {
      customerStatesByCountry[country] = normalized
    }
  }

  const customerStates = [...new Set(Object.values(customerStatesByCountry).flat())].sort()
  return { customerStates, customerStatesByCountry }
}

export function mapSalesFilterOptionsFromApi(data: {
  order_status?: string[]
  product_status?: string[]
  invoice_status?: string[]
  gl_code?: string[]
  gl_name?: string[]
  customer_states?: string[] | Record<string, string[]>
  account_groups?: string[]
  sales_reps?: {
    rep_key: string
    sales_rep_name: string
    sales_rep_email: string | null
    team_uuid: string | null
    filter_keys?: string[]
  }[]
}): SalesFilterOptions {
  const { customerStates, customerStatesByCountry } = normalizeCustomerStatesFromApi(
    data.customer_states
  )

  return {
    orderStatuses: data.order_status ?? [],
    productStatuses: data.product_status ?? [],
    invoiceStatuses: data.invoice_status ?? [],
    glCodes: data.gl_code ?? [],
    glNames: data.gl_name ?? [],
    customerStates,
    customerStatesByCountry,
    accountGroups: data.account_groups ?? [],
    salesReps: (data.sales_reps ?? []).map((rep) => ({
      repKey: rep.rep_key,
      salesRepName: rep.sales_rep_name,
      salesRepEmail: rep.sales_rep_email,
      teamUuid: rep.team_uuid,
      filterKeys: rep.filter_keys?.length ? [...rep.filter_keys] : [rep.rep_key],
    })),
  }
}

/** @deprecated Use mapSalesFilterOptionsFromApi */
export const mapPerformanceFilterOptionsFromApi = mapSalesFilterOptionsFromApi

export function buildDefaultSalesDashboardFilters(
  options: SalesFilterOptions
): SalesDashboardFilters {
  const statusDefaults = buildDefaultSalesStatusFilters({
    order: options.orderStatuses,
    product: options.productStatuses,
    invoice: options.invoiceStatuses,
  })
  return {
    mis: 'both',
    teamUuids: [],
    teamMemberUuids: [],
    repKeys: [],
    orderStatuses: [...statusDefaults.orderStatuses],
    productStatuses: [...statusDefaults.productStatuses],
    invoiceStatuses: [...statusDefaults.invoiceStatuses],
    glCodes: [],
    customerStates: [],
    accountGroups: [],
  }
}

/** @deprecated Use buildDefaultSalesDashboardFilters */
export const buildDefaultPerformanceFilters = buildDefaultSalesDashboardFilters

export function sanitizeSalesStatusFilters(
  filters: SalesDashboardFilters,
  options: SalesDashboardStatusOptions
): Pick<SalesDashboardFilters, 'orderStatuses' | 'productStatuses' | 'invoiceStatuses'> {
  const orderSet = new Set(options.order)
  const productSet = new Set(options.product)
  const invoiceSet = new Set(options.invoice)
  return {
    orderStatuses: filters.orderStatuses.filter((s) => orderSet.has(s)),
    productStatuses: filters.productStatuses.filter((s) => productSet.has(s)),
    invoiceStatuses: filters.invoiceStatuses.filter((s) => invoiceSet.has(s)),
  }
}

/** @deprecated Use sanitizeSalesStatusFilters */
export const sanitizePerformanceStatusFilters = sanitizeSalesStatusFilters

function sortedKey(values: string[]): string {
  return [...values].sort().join('\0')
}

function arraysEqualSorted(a: string[], b: string[]): boolean {
  return sortedKey(a) === sortedKey(b)
}

export function salesStatusDefaultsEqual(
  filters: Pick<SalesDashboardFilters, 'orderStatuses' | 'productStatuses' | 'invoiceStatuses'>,
  options: SalesDashboardStatusOptions
): boolean {
  const defaults = buildDefaultSalesStatusFilters(options)
  return (
    arraysEqualSorted(filters.orderStatuses, defaults.orderStatuses) &&
    arraysEqualSorted(filters.productStatuses, defaults.productStatuses) &&
    arraysEqualSorted(filters.invoiceStatuses, defaults.invoiceStatuses)
  )
}

/** @deprecated Use salesStatusDefaultsEqual */
export const performanceStatusDefaultsEqual = salesStatusDefaultsEqual

export function countAppliedSalesDashboardFilters(
  filters: SalesDashboardFilters,
  statusOptions?: SalesDashboardStatusOptions
): number {
  let count = 0
  if (filters.mis !== 'both') count += 1
  if (filters.teamUuids.length > 0) count += 1
  if (filters.teamMemberUuids.length > 0) count += 1
  if (filters.repKeys.length > 0) count += 1
  if (filters.glCodes.length > 0) count += 1
  if (filters.customerStates.length > 0) count += 1
  if (filters.accountGroups.length > 0) count += 1

  if (statusOptions) {
    if (!salesStatusDefaultsEqual(filters, statusOptions)) count += 1
  } else {
    if (filters.orderStatuses.length > 0) count += 1
    if (filters.productStatuses.length > 0) count += 1
    if (filters.invoiceStatuses.length > 0) count += 1
  }

  return count
}

/** @deprecated Use countAppliedSalesDashboardFilters */
export const countAppliedPerformanceFilters = countAppliedSalesDashboardFilters

export function misFilterToApiParam(mis: SalesDashboardMisFilter): 'CB' | 'PIQ' | undefined {
  if (mis === 'cb') return 'CB'
  if (mis === 'piq') return 'PIQ'
  return undefined
}

function appendCustomerStatesToParams(
  params: DashboardPagesQueryParams,
  customerStates: string[]
): void {
  if (customerStates.length === 0) return
  if (customerStates.length === 1) {
    params.state = customerStates[0]
    return
  }
  params.states = [...customerStates]
}

export function salesDashboardDrawerShowsSalesRep(pageSlug: string): boolean {
  return (
    pageSlug === 'leaderboard' ||
    pageSlug === 'sale-detail' ||
    pageSlug === 'texas-sales-by-rep' ||
    pageSlug === 'open-pipeline'
  )
}

export function salesDashboardDrawerShowsCustomerState(pageSlug: string): boolean {
  return pageSlug === 'sale-detail'
}

/**
 * Account-group filter is only offered on the main Sale Detail page.
 */
export function salesDashboardDrawerShowsAccountGroup(pageSlug: string): boolean {
  return pageSlug === 'sale-detail'
}

export function salesDashboardDrawerShowsTeamFilters(pageSlug: string): boolean {
  return pageSlug !== 'amazon-dashboard'
}

export function salesDashboardDrawerShowsTimeRange(pageSlug: string): boolean {
  const cfg = getDashboardPageConfig(pageSlug)
  return cfg.isPerformance === true || cfg.headerProfile.dateRange === true
}

export function salesDashboardDrawerShowsExport(pageSlug: string): boolean {
  const cfg = getDashboardPageConfig(pageSlug)
  return cfg.isPerformance === true || cfg.headerProfile.export === true
}

/** True when applied time range differs from session default (month, empty custom). */
export function isAppliedTimeRangeNonDefault(
  timeRange: SalesTimeRangePreset,
  customRange: SalesDashboardCustomRange
): boolean {
  if (timeRange !== 'month') return true
  return Boolean(customRange.from || customRange.to)
}

export function appendSalesDashboardFiltersToParams(
  params: DashboardPagesQueryParams,
  filters: SalesDashboardFilters,
  options: {
    page: SalesDashboardFilterPage
    salesMode?: SalesMode
    salesReps?: SalesDashboardSalesRepOption[]
  }
): void {
  const { page, salesMode, salesReps } = options

  if (page === 'open-pipeline') {
    if (filters.teamUuids.length === 1) params.team_uuid = filters.teamUuids[0]
    if (filters.teamMemberUuids.length === 1) params.team_member_uuid = filters.teamMemberUuids[0]
    if (filters.repKeys.length > 0) params.rep_keys = [...filters.repKeys]
    appendCustomerStatesToParams(params, filters.customerStates)
    return
  }

  if (page === 'amazon-dashboard') {
    const mis = misFilterToApiParam(filters.mis)
    if (mis) params.mis = mis
    if (salesMode === 'created') {
      if (filters.orderStatuses.length > 0) params.order_status = [...filters.orderStatuses]
      if (filters.productStatuses.length > 0) params.product_status = [...filters.productStatuses]
    }
    if (salesMode === 'invoiced') {
      if (filters.invoiceStatuses.length > 0) params.invoice_status = [...filters.invoiceStatuses]
      if (filters.glCodes.length > 0) params.gl_code = [...filters.glCodes]
    }
    return
  }

  const mis = misFilterToApiParam(filters.mis)
  if (mis) params.mis = mis
  if (filters.teamUuids.length > 0) params.team_uuids = [...filters.teamUuids]
  if (
    (page === 'leaderboard' || page === 'sale-detail' || page === 'performance') &&
    filters.teamMemberUuids.length > 0
  ) {
    params.team_member_uuids = [...filters.teamMemberUuids]
  }

  if (page === 'leaderboard' || page === 'sale-detail') {
    if (filters.repKeys.length > 0) {
      params.rep_keys =
        salesReps && salesReps.length > 0
          ? expandRepKeysForApi(filters.repKeys, salesReps)
          : [...filters.repKeys]
    }
  }

  const includeCreatedStatuses =
    page === 'performance' || salesMode === 'created'
  const includeInvoicedStatuses =
    page === 'performance' || salesMode === 'invoiced'

  if (includeCreatedStatuses) {
    if (filters.orderStatuses.length > 0) params.order_status = [...filters.orderStatuses]
    if (filters.productStatuses.length > 0) params.product_status = [...filters.productStatuses]
  }
  if (includeInvoicedStatuses) {
    if (filters.invoiceStatuses.length > 0) params.invoice_status = [...filters.invoiceStatuses]
    if (filters.glCodes.length > 0) params.gl_code = [...filters.glCodes]
  }

  if (page === 'sale-detail') {
    appendCustomerStatesToParams(params, filters.customerStates)
  }
}

/** @deprecated Use appendSalesDashboardFiltersToParams */
export function appendPerformanceFiltersToParams(
  params: DashboardPagesQueryParams,
  filters: SalesDashboardFilters
): void {
  appendSalesDashboardFiltersToParams(params, filters, { page: 'performance' })
}

export function appendSalesTimeRangeToParams(
  params: DashboardPagesQueryParams,
  timeRange: SalesTimeRangePreset,
  customRange: SalesDashboardCustomRange,
  selectedMonth: string
): void {
  if (timeRange === 'month') {
    params.period = 'month'
    const [yearStr, monthStr] = selectedMonth.split('-')
    params.year = Number(yearStr)
    params.month = Number(monthStr)
    return
  }

  params.period = timeRange
  if (timeRange === 'custom') {
    if (customRange.from) params.start_date = customRange.from
    if (customRange.to) params.end_date = customRange.to
  }
}

export function salesStatusOptionItemId(groupId: string, value: string): string {
  return `${groupId}::${value}`
}

/** @deprecated Use salesStatusOptionItemId */
export const performanceStatusOptionItemId = salesStatusOptionItemId

export function parseSalesStatusOptionItemId(
  itemId: string
): { groupId: string; value: string } | null {
  const sep = itemId.lastIndexOf('::')
  if (sep < 0) return null
  return { groupId: itemId.slice(0, sep), value: itemId.slice(sep + 2) }
}

/** @deprecated Use parseSalesStatusOptionItemId */
export const parsePerformanceStatusOptionItemId = parseSalesStatusOptionItemId

function maybeSelectAllInGroup(
  groupIds: string[],
  itemIds: string[],
  groupKey: string,
  selected: string[],
  options: string[]
): void {
  if (
    selected.length > 0 &&
    options.length > 0 &&
    selected.length === options.length &&
    options.every((v) => selected.includes(v))
  ) {
    groupIds.push(groupKey)
  } else {
    for (const value of selected) {
      itemIds.push(salesStatusOptionItemId(groupKey, value))
    }
  }
}

export function buildSalesStatusGroup(
  groupId: string,
  groupLabel: string,
  options: string[]
): GroupedCheckboxMultiSelectGroup {
  return {
    groupId,
    groupLabel,
    items: options.map((status) => ({
      itemId: salesStatusOptionItemId(groupId, status),
      itemLabel: status,
    })),
  }
}

/** @deprecated Use buildSalesStatusGroup */
export const buildPerformanceStatusGroup = buildSalesStatusGroup

export function salesSingleGroupArrayToGroupedSelection(
  groupId: string,
  selected: string[],
  options: string[]
): GroupedCheckboxMultiSelectValue {
  const groupIds: string[] = []
  const itemIds: string[] = []
  maybeSelectAllInGroup(groupIds, itemIds, groupId, selected, options)
  return { groupIds, itemIds }
}

/** @deprecated Use salesSingleGroupArrayToGroupedSelection */
export const performanceSingleGroupArrayToGroupedSelection = salesSingleGroupArrayToGroupedSelection

export function groupedSelectionToSalesSingleGroupArray(
  selection: GroupedCheckboxMultiSelectValue,
  groupId: string,
  options: string[]
): string[] {
  const values = new Set<string>()
  const optionSet = new Set(options)
  if (selection.groupIds.includes(groupId) && options.length > 0) {
    for (const value of options) values.add(value)
  }
  for (const itemId of selection.itemIds) {
    const parsed = parseSalesStatusOptionItemId(itemId)
    if (parsed?.groupId === groupId && optionSet.has(parsed.value)) {
      values.add(parsed.value)
    }
  }
  return [...values]
}

/** @deprecated Use groupedSelectionToSalesSingleGroupArray */
export const groupedSelectionToPerformanceSingleGroupArray = groupedSelectionToSalesSingleGroupArray

export function salesGlArrayToGroupedSelection(
  gl: string[],
  glOptions: string[]
): GroupedCheckboxMultiSelectValue {
  return salesSingleGroupArrayToGroupedSelection(SALES_STATUS_GROUP_GL, gl, glOptions)
}

/** @deprecated Use salesGlArrayToGroupedSelection */
export const performanceGlArrayToGroupedSelection = salesGlArrayToGroupedSelection

export function groupedSelectionToSalesGlArray(
  selection: GroupedCheckboxMultiSelectValue,
  glOptions: string[]
): string[] {
  return groupedSelectionToSalesSingleGroupArray(selection, SALES_STATUS_GROUP_GL, glOptions)
}

/** @deprecated Use groupedSelectionToSalesGlArray */
export const groupedSelectionToPerformanceGlArray = groupedSelectionToSalesGlArray

export function buildSalesFlatFilterGroup(
  groupId: string,
  groupLabel: string,
  options: { id: string; label: string }[]
): GroupedCheckboxMultiSelectGroup {
  return {
    groupId,
    groupLabel,
    items: options.map((opt) => ({
      itemId: salesStatusOptionItemId(groupId, opt.id),
      itemLabel: opt.label,
    })),
  }
}

export function flatIdsToGroupedSelection(
  groupId: string,
  selectedIds: string[],
  options: { id: string }[]
): GroupedCheckboxMultiSelectValue {
  const optionIds = options.map((o) => o.id)
  return salesSingleGroupArrayToGroupedSelection(groupId, selectedIds, optionIds)
}

export function groupedSelectionToFlatIds(
  selection: GroupedCheckboxMultiSelectValue,
  groupId: string,
  options: { id: string }[]
): string[] {
  const optionIds = options.map((o) => o.id)
  return groupedSelectionToSalesSingleGroupArray(selection, groupId, optionIds)
}

/** Build a grouped-checkbox group id for a customer-state country column (USA, Canada). */
export function buildCustomerStateCountryGroupId(country: string): string {
  return salesStatusOptionItemId(SALES_FILTER_GROUP_CUSTOMER_STATE, country)
}

const CUSTOMER_STATE_COUNTRY_COLUMN_ORDER = ['USA', 'Canada', 'Unassigned']

/** Sort index for customer-state country columns (USA first, Canada second). */
function customerStateCountrySortIndex(country: string): number {
  const index = CUSTOMER_STATE_COUNTRY_COLUMN_ORDER.indexOf(country)
  return index >= 0 ? index : CUSTOMER_STATE_COUNTRY_COLUMN_ORDER.length
}

/** Build grouped Customer State filter options (country header + state checkboxes). */
export function buildCustomerStateCountryGroups(
  byCountry: Record<string, string[]>
): GroupedCheckboxMultiSelectGroup[] {
  return Object.entries(byCountry)
    .sort(([a], [b]) => {
      const orderDiff = customerStateCountrySortIndex(a) - customerStateCountrySortIndex(b)
      return orderDiff !== 0 ? orderDiff : a.localeCompare(b)
    })
    .map(([country, states]) => ({
      groupId: buildCustomerStateCountryGroupId(country),
      groupLabel: country,
      items: states.map((state) => ({
        itemId: salesStatusOptionItemId(buildCustomerStateCountryGroupId(country), state),
        itemLabel: state,
      })),
    }))
}

/** Map flat customer state codes to grouped checkbox selection (group + item ids). */
export function customerStatesToGroupedSelection(
  states: string[],
  groups: GroupedCheckboxMultiSelectGroup[]
): GroupedCheckboxMultiSelectValue {
  const groupIds: string[] = []
  const itemIds: string[] = []
  const selected = new Set(states)

  for (const group of groups) {
    const options = group.items.map((item) => {
      const parsed = parseSalesStatusOptionItemId(item.itemId)
      return parsed?.value ?? item.itemId
    })
    const selectedInGroup = options.filter((value) => selected.has(value))
    maybeSelectAllInGroup(groupIds, itemIds, group.groupId, selectedInGroup, options)
  }

  return { groupIds, itemIds }
}

/** Map grouped checkbox selection back to flat customer state codes for API/store. */
export function groupedSelectionToCustomerStates(
  selection: GroupedCheckboxMultiSelectValue,
  groups: GroupedCheckboxMultiSelectGroup[]
): string[] {
  const values = new Set<string>()

  for (const group of groups) {
    const options = group.items.map((item) => {
      const parsed = parseSalesStatusOptionItemId(item.itemId)
      return parsed?.value ?? item.itemId
    })
    const optionSet = new Set(options)

    if (selection.groupIds.includes(group.groupId) && options.length > 0) {
      for (const value of options) values.add(value)
    }

    for (const itemId of selection.itemIds) {
      const parsed = parseSalesStatusOptionItemId(itemId)
      if (parsed?.groupId === group.groupId && optionSet.has(parsed.value)) {
        values.add(parsed.value)
      }
    }
  }

  return [...values].sort()
}

export function pruneTeamMemberUuidsForTeams(
  teamMemberUuids: string[],
  teamUuids: string[],
  channelTeams: { uuid: string; members: { uuid: string }[] }[]
): string[] {
  if (teamUuids.length === 0) return teamMemberUuids
  const validRepUuids = new Set(
    channelTeams
      .filter((team) => teamUuids.includes(team.uuid))
      .flatMap((team) => team.members.map((member) => member.uuid))
  )
  return teamMemberUuids.filter((uuid) => validRepUuids.has(uuid))
}

export function pruneRepKeysForTeams(
  repKeys: string[],
  teamUuids: string[],
  salesReps: { repKey: string; teamUuid: string | null }[]
): string[] {
  if (teamUuids.length === 0) return repKeys
  const teamSet = new Set(teamUuids)
  const validRepKeys = new Set(
    salesReps
      .filter((rep) => rep.teamUuid !== null && teamSet.has(rep.teamUuid))
      .map((rep) => rep.repKey)
  )
  return repKeys.filter((key) => validRepKeys.has(key))
}

export function applyDashboardBodyFilters<
  T extends {
    team?: string
    repId?: string | null
    customerState?: string
    state?: string
    accountName?: string
  },
>(rows: T[], filters: SalesDashboardFilters): T[] {
  return rows.filter((row) => {
    if (filters.teamUuids.length > 0 && row.team && !filters.teamUuids.includes(row.team)) {
      return false
    }
    if (
      filters.repKeys.length > 0 &&
      row.repId &&
      !filters.repKeys.includes(row.repId)
    ) {
      return false
    }
    if (
      filters.teamMemberUuids.length > 0 &&
      row.repId &&
      !filters.teamMemberUuids.includes(row.repId)
    ) {
      return false
    }
    const stateValue = row.customerState ?? row.state
    if (
      filters.customerStates.length > 0 &&
      stateValue &&
      !filters.customerStates.includes(stateValue)
    ) {
      return false
    }
    if (
      filters.accountGroups.length > 0 &&
      row.accountName &&
      !filters.accountGroups.includes(row.accountName)
    ) {
      return false
    }
    return true
  })
}
